import getDashboardUrl from 'constants/dashboard';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormikActions } from 'formik';
import { withRouter, type RouteComponentProps } from 'react-router-dom';

import SiteFooter from 'components/SiteFooter';
import SiteHeader from 'components/SiteHeader';
import AccountForm from 'components/forms/AccountForm';
import PasswordForm from 'components/forms/PasswordForm';
import LocationForm from 'components/forms/LocationForm';
import WizardForm from 'components/forms/WizardForm';
import { useI18n } from 'i18n/I18nContext';
import { withLocalePrefix } from 'i18n/localePath';
import background from 'static/images/background@2x.jpg';
import {
  DEFAULT_ONBOARD_FORM_VALUES,
  ONBOARD_OAUTH_STORAGE_KEY,
  ONBOARD_TOKENS_STORAGE_KEY,
  type OnboardAuthMode,
  type OnboardFormValues,
  type OnboardOAuthDraft,
  type OnboardStoredTokens,
} from 'types/onboard';
import {
  identityHashHasError,
  isIdentityConfigured,
  parseIdentityHashTokens,
  peekJwtPayload,
  startGoogleOAuth,
} from 'utils/identity';

const API_URL = process.env.REACT_APP_API_URL || '';
const ONBOARD_PATH = '/api/auth/onboard';
const ONBOARD_COMPLETE_PATH = '/api/auth/onboard-complete';

interface ApiErrorPayload {
  error?: string;
  validation?: Record<string, string[]>;
}

function readCheckoutSessionId(search: string): string | null {
  const match = /(?:^\?|&)session_id=([^&]+)/.exec(search || '');
  if (!match || !match[1]) return null;
  try {
    const id = decodeURIComponent(match[1]).trim();
    return id.startsWith('cs_') ? id : null;
  } catch {
    return null;
  }
}

function readStoredTokens(): OnboardStoredTokens | null {
  try {
    const raw = sessionStorage.getItem(ONBOARD_TOKENS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardStoredTokens;
    return parsed?.access_token ? parsed : null;
  } catch {
    return null;
  }
}

function OnboardPage({ location }: RouteComponentProps) {
  const { t, locale } = useI18n();
  const checkoutSessionId = useMemo(
    () => readCheckoutSessionId(location.search || ''),
    [location.search]
  );
  const [formValues, setFormValues] = useState<OnboardFormValues>({
    ...DEFAULT_ONBOARD_FORM_VALUES,
  });
  const [success, setSuccess] = useState(false);
  const [authMode, setAuthMode] = useState<OnboardAuthMode>('password');
  const [initialPage, setInitialPage] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [oauthBootError, setOauthBootError] = useState<string | null>(null);
  const googleEnabled = isIdentityConfigured();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    if (identityHashHasError(hash)) {
      setOauthBootError(t('forms.googleFailed'));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }
    const tokens = parseIdentityHashTokens(hash);
    if (!tokens?.access_token) {
      const existing = readStoredTokens();
      if (existing?.access_token) {
        setAuthMode('google');
        setInitialPage(2);
        const payload = peekJwtPayload(existing.access_token);
        setFormValues((prev) => ({
          ...prev,
          email: payload?.email || prev.email,
          name: payload?.user_metadata?.full_name || payload?.user_metadata?.name || prev.name,
        }));
      }
      return;
    }

    try {
      sessionStorage.setItem(ONBOARD_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
      const draftRaw = sessionStorage.getItem(ONBOARD_OAUTH_STORAGE_KEY);
      if (draftRaw) {
        const draft = JSON.parse(draftRaw) as OnboardOAuthDraft;
        if (draft.checkoutSessionId && !checkoutSessionId) {
          const next = `${window.location.pathname}?session_id=${encodeURIComponent(draft.checkoutSessionId)}`;
          window.history.replaceState(null, '', next);
        }
      }
    } catch {
      /* ignore storage failures */
    }

    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setAuthMode('google');
    setInitialPage(2);
    const payload = peekJwtPayload(tokens.access_token);
    setFormValues((prev) => ({
      ...prev,
      email: payload?.email || prev.email,
      name: payload?.user_metadata?.full_name || payload?.user_metadata?.name || prev.name,
    }));
  }, [checkoutSessionId, t]);

  const handleGoogleClick = useCallback(() => {
    if (!googleEnabled) return;
    setGoogleLoading(true);
    setOauthBootError(null);
    const returnPath = withLocalePrefix(locale, '/onboard');
    const qs = checkoutSessionId ? `?session_id=${encodeURIComponent(checkoutSessionId)}` : '';
    const redirectTo = `${window.location.origin}${returnPath}${qs}`;
    const draft: OnboardOAuthDraft = {
      intent: 'onboard',
      checkoutSessionId,
      returnPath: `${returnPath}${qs}`,
    };
    try {
      sessionStorage.setItem(ONBOARD_OAUTH_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* continue */
    }
    startGoogleOAuth(redirectTo);
  }, [checkoutSessionId, googleEnabled, locale]);

  const handleValidationErrors = useCallback(
    (
      err: ApiErrorPayload,
      setFieldError: FormikActions<OnboardFormValues>['setFieldError'],
      resetPageIndex: (() => void) | null | undefined
    ) => {
      const validation = err?.validation;
      if (validation) {
        Object.keys(validation).forEach((field) => {
          const messages = validation[field];
          if (Array.isArray(messages) && messages[0]) {
            setFieldError(field, messages[0]);
          }
        });
        const accountFields = ['name', 'email'];
        const passwordFields = ['password'];
        const keys = Object.keys(validation);
        if (keys.some((r) => accountFields.includes(r)) && resetPageIndex) {
          resetPageIndex();
        } else if (keys.some((r) => passwordFields.includes(r)) && resetPageIndex) {
          resetPageIndex();
        }
        return;
      }
      if (err?.error && setFieldError) {
        setFieldError('_form', err.error);
      }
    },
    []
  );

  const submitPasswordOnboard = useCallback(
    async (
      values: OnboardFormValues,
      bag: FormikActions<OnboardFormValues>,
      resetPageIndex: (() => void) | null | undefined
    ) => {
      const { setFieldError, setSubmitting } = bag;
      const body = {
        name: values.name,
        email: values.email,
        password: values.password,
        locationName: values.locationName,
        address: values.address || '',
        city: values.city || '',
        zip: values.zip || '',
        phone: values.phone || '',
        ...(checkoutSessionId ? { checkoutSessionId } : {}),
      };
      try {
        const res = await fetch(`${API_URL}${ONBOARD_PATH}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = (await res.json().catch(() => ({}))) as ApiErrorPayload;
        if (!res.ok) {
          handleValidationErrors(
            { error: data.error || t('forms.requestFailed'), validation: data.validation },
            setFieldError,
            resetPageIndex
          );
          setSubmitting(false);
          return;
        }
        setSuccess(true);
        setSubmitting(false);
      } catch (caught: unknown) {
        const message = caught instanceof Error ? caught.message : t('forms.networkError');
        setFieldError('_form', message);
        setSubmitting(false);
      }
    },
    [handleValidationErrors, t, checkoutSessionId]
  );

  const submitGoogleOnboard = useCallback(
    async (
      values: OnboardFormValues,
      bag: FormikActions<OnboardFormValues>,
      _resetPageIndex: (() => void) | null | undefined
    ) => {
      const { setFieldError, setSubmitting } = bag;
      const tokens = readStoredTokens();
      if (!tokens?.access_token) {
        setFieldError('_form', t('forms.googleSessionExpired'));
        setSubmitting(false);
        setAuthMode('password');
        setInitialPage(0);
        return;
      }
      const body = {
        locationName: values.locationName,
        address: values.address || '',
        city: values.city || '',
        zip: values.zip || '',
        phone: values.phone || '',
        ...(checkoutSessionId ? { checkoutSessionId } : {}),
        ...(values.name ? { name: values.name } : {}),
      };
      try {
        const res = await fetch(`${API_URL}${ONBOARD_COMPLETE_PATH}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.access_token}`,
          },
          body: JSON.stringify(body),
        });
        const data = (await res.json().catch(() => ({}))) as ApiErrorPayload & {
          dashboard?: boolean;
        };
        if (res.status === 409) {
          try {
            sessionStorage.removeItem(ONBOARD_TOKENS_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          window.location.assign(getDashboardUrl());
          return;
        }
        if (!res.ok) {
          setFieldError('_form', data.error || t('forms.requestFailed'));
          setSubmitting(false);
          return;
        }
        try {
          sessionStorage.removeItem(ONBOARD_TOKENS_STORAGE_KEY);
          sessionStorage.removeItem(ONBOARD_OAUTH_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setSuccess(true);
        setSubmitting(false);
      } catch (caught: unknown) {
        const message = caught instanceof Error ? caught.message : t('forms.networkError');
        setFieldError('_form', message);
        setSubmitting(false);
      }
    },
    [t, checkoutSessionId]
  );

  const renderSuccess = () => (
    <div className="gb-onboard-success">
      <h1 className="gb-onboard-panel__title">{t('onboard.thankYouTitle')}</h1>
      <p className="gb-onboard-success__body">{t('onboard.thankYouBodyBefore')}</p>
      <a href={getDashboardUrl()} className="gb-onboard-success__cta gb-btn">
        {t('onboard.thankYouLink')}
      </a>
    </div>
  );

  return (
    <div
      className="gb-onboard"
      style={
        {
          ['--gb-onboard-bg-image' as string]: `url(${background})`,
        } as React.CSSProperties
      }
    >
      <SiteHeader variant="onHero" />

      <main className="gb-onboard__main">
        <div className="gb-onboard-panel">
          {success ? (
            renderSuccess()
          ) : (
            <>
              {oauthBootError ? (
                <div className="gb-onboard-alert" role="alert">
                  {oauthBootError}
                </div>
              ) : null}
              <WizardForm
                key={`${authMode}-${initialPage}`}
                initialValues={formValues}
                initialPage={initialPage}
                authMode={authMode}
                checkoutClaim={Boolean(checkoutSessionId)}
                googleEnabled={googleEnabled && authMode === 'password'}
                googleLoading={googleLoading}
                onGoogleClick={handleGoogleClick}
                onSubmit={(values, bag, resetPageIndex) => {
                  setFormValues(values);
                  if (authMode === 'google') {
                    submitGoogleOnboard(values, bag, resetPageIndex);
                  } else {
                    submitPasswordOnboard(values, bag, resetPageIndex);
                  }
                }}
              >
                <AccountForm />
                <PasswordForm />
                <LocationForm />
              </WizardForm>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default withRouter(OnboardPage);



// WEBPACK FOOTER //
// ./src/pages/Onboard.tsx